import { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Collapsible,
  Data,
  DataFilter,
  DataFilters,
  Grid,
  ResponsiveContext,
  TextInput, // Import TextInput component
} from 'grommet';

import { Down, Next } from 'grommet-icons';
import { ListResults } from '../ListResults';

const allAbstracts = require('../../../data/archive/abstracts.json');

const pageContentGrid = {
  columns: {
    xsmall: 'auto',
    small: 'auto',
    medium: [['small', '1/3'], 'flex'],
    large: [['20%', '25%'], 'flex'],
    xlarge: [['1/4', 'medium'], 'flex'],
  },
  gap: {
    xsmall: 'medium',
    small: 'medium',
    medium: 'medium',
    large: 'large',
    xlarge: 'large',
  },
};

export const AbstractsComp = () => {
  return (
    <Box pad={{ top: 'medium' }}>
      <ContentLayout />
    </Box>
  );
};

const properties = {
  status: {
    label: 'Status',
    options: [
      { label: 'Formal Presentation', value: 'Formal Presentation' },
      { label: 'Poster', value: 'Poster' },
      { label: 'Lightning Round', value: 'Lightning Round' },
      { label: 'Challenge', value: 'Challenge' },
      { label: 'Implemented', value: 'Implemented' },
      { label: 'Pending', value: 'Pending' },
      { label: 'Not Selected', value: 'Not Selected' },
    ],
  },
  stage: {
    label: 'Stage',
    options: [
      { label: 'Project Submission', value: 'Project Submission' },
      { label: 'Hackathon', value: 'Hackathon' },
      { label: 'Results', value: 'Results' },
    ],
  },
  Category: {
    label: 'Category',
    options: [
      { label: 'General Abstract', value: 'General Abstract' },
      { label: 'Challenge 1: Unified data plane', value: 'Challenge 1: Unified data plane' },
      { label: 'Challenge 2: Enterprise IoT', value: 'Challenge 2: Enterprise IoT' },
      { label: '...', value: '...' },
    ],
  },
  sustainability: { label: 'Sustainability' },
  security: { label: 'Security' },
  tech: { label: 'Technology' },
  delivery: { label: 'Delivery Methods' },
  pricing: { label: 'Business Models' },
};

const Filter = ({ label, expand, setExpand, property }) => {
  return (
    <>
      <Button
        label={label}
        icon={expand ? <Down /> : <Next />}
        onClick={() => setExpand(!expand)}
      />
      <Collapsible open={expand}>
        <Box pad={{ left: 'medium', top: 'xsmall' }}>
          <DataFilter property={property} />
        </Box>
      </Collapsible>
    </>
  );
};

Filter.propTypes = {
  label: PropTypes.string.isRequired,
  expand: PropTypes.bool.isRequired,
  setExpand: PropTypes.func.isRequired,
  property: PropTypes.string.isRequired,
};

const ContentLayout = () => {
  const breakpoint = useContext(ResponsiveContext);
  const showFiltersButton = ['xsmall', 'small'].includes(breakpoint);

  const [expandStatus, setExpandStatus] = useState(false);
  const [expandStage, setExpandStage] = useState(false);
  const [expandCategory, setExpandCategory] = useState(false);
  const [expandTechnology, setExpandTechnology] = useState(false);
  const [expandPricing, setExpandPricing] = useState(false);
  const [expandSustainability, setExpandSustainability] = useState(false);
  
  // New state for the search input
  const [searchQuery, setSearchQuery] = useState('');

  // Function to filter abstracts based on search query
  const filteredAbstracts = allAbstracts.filter(abstract => {
    return (
      abstract.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      abstract.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      abstract.category.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const sidebar = showFiltersButton ? (
    <DataFilters layer />
  ) : (
    <Box
      direction="column"
      align="start"
      gap="small"
      background="background-front"
      pad={{ right: 'small', vertical: 'small' }}
      round="xsmall"
    >
      {/* Add the TextInput here for the search filter */}
      <Box pad={{ left: 'small' }} width="medium">
        <TextInput
          placeholder="Search abstracts..."
          value={searchQuery}
          onChange={event => setSearchQuery(event.target.value)} // Update the search query state
        />
      </Box>

      <Filter
        label="Status"
        expand={expandStatus}
        setExpand={setExpandStatus}
        property="status"
      />
      <Filter
        label="Stage"
        expand={expandStage}
        setExpand={setExpandStage}
        property="stage"
      />
      <Filter
        label="Category"
        expand={expandCategory}
        setExpand={setExpandCategory}
        property="Category"
      />
      <Filter
        label="Technology Area"
        expand={expandTechnology}
        setExpand={setExpandTechnology}
        property="tech"
      />
      <Filter
        label="Sustainability"
        expand={expandSustainability}
        setExpand={setExpandSustainability}
        property="sustainability"
      />
      <Filter
        label="Business Models"
        expand={expandPricing}
        setExpand={setExpandPricing}
        property="pricing"
      />
    </Box>
  );

  return (
    <Data
      data={filteredAbstracts} // Use filtered abstracts based on search query
      properties={properties}
      updateOn={showFiltersButton ? undefined : 'change'}
    >
      <Grid
        align="start"
        columns={pageContentGrid.columns[breakpoint]}
        gap={pageContentGrid.gap[breakpoint]}
      >
        {sidebar}
        <ListResults />
      </Grid>
    </Data>
  );
};
